import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Card,
  CardContent,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Visibility,
  AccountBalanceWallet,
  Receipt,
  AttachMoney,
} from '@mui/icons-material';
import { DataGrid } from '@mui/x-data-grid';
import axios from 'axios';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';

const API_URL = process.env.REACT_APP_API_URL || 'http://192.168.112.10:8000/api';

export default function Cajas() {
  const [cajas, setCajas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [cajaSeleccionada, setCajaSeleccionada] = useState(null);
  const [boletas, setBoletas] = useState([]);
  const [error, setError] = useState('');

  useEffect(() => {
    cargarDatos();
  }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      const response = await axios.get(`${API_URL}/cajas`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCajas(response.data.data || response.data);
    } catch (error) {
      console.error('Error al cargar cajas:', error);
      setError('Error al cargar las cajas');
    } finally {
      setLoading(false);
    }
  };

  const handleVerDetalle = async (caja) => {
    try {
      const token = localStorage.getItem('admin_token');
      
      // Cargar detalle de la caja
      const [cajaResponse, boletasResponse] = await Promise.all([
        axios.get(`${API_URL}/cajas/${caja.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/cajas/${caja.id}/boletas`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      
      setCajaSeleccionada(cajaResponse.data);
      setBoletas(boletasResponse.data);
      setOpenDialog(true);
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      setError('Error al cargar el detalle de la caja');
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCajaSeleccionada(null);
    setBoletas([]);
  };

  const getEstadoColor = (estado) => {
    const colors = {
      'abierta': 'success',
      'cerrada': 'warning',
      'rendida': 'info',
    };
    return colors[estado] || 'default';
  };

  const formatFecha = (fecha) => {
    if (!fecha) return '-';
    return format(new Date(fecha), 'dd/MM/yyyy HH:mm', { locale: es });
  };

  const formatMonto = (monto) => {
    return `Gs. ${parseFloat(monto || 0).toLocaleString('es-PY')}`;
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'conductor',
      headerName: 'Conductor',
      width: 200,
      valueGetter: (params) => params.row.user?.name || params.row.user?.nombre || '-',
    },
    {
      field: 'fecha_apertura',
      headerName: 'Fecha Apertura',
      width: 180,
      valueFormatter: (params) => formatFecha(params.value),
    },
    {
      field: 'fecha_cierre',
      headerName: 'Fecha Cierre',
      width: 180,
      valueFormatter: (params) => formatFecha(params.value),
    },
    {
      field: 'monto_inicial',
      headerName: 'Monto Inicial',
      width: 130,
      valueFormatter: (params) => formatMonto(params.value),
    },
    {
      field: 'monto_final',
      headerName: 'Monto Final',
      width: 130,
      valueFormatter: (params) => formatMonto(params.value),
    },
    {
      field: 'estado',
      headerName: 'Estado',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value.toUpperCase()}
          color={getEstadoColor(params.value)}
          size="small"
        />
      ),
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 100,
      renderCell: (params) => (
        <IconButton
          color="primary"
          onClick={() => handleVerDetalle(params.row)}
          size="small"
        >
          <Visibility />
        </IconButton>
      ),
    },
  ];

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Gestión de Cajas
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={cajas}
          columns={columns}
          pageSize={10}
          rowsPerPageOptions={[10, 25, 50]}
          disableSelectionOnClick
        />
      </Paper>

      {/* Dialog de Detalle */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          Detalle de Caja #{cajaSeleccionada?.id}
        </DialogTitle>
        <DialogContent>
          {cajaSeleccionada && (
            <Box sx={{ mt: 2 }}>
              {/* Información General */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Conductor
                      </Typography>
                      <Typography variant="h6">
                        {cajaSeleccionada.conductor}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Estado
                      </Typography>
                      <Chip
                        label={cajaSeleccionada.estado.toUpperCase()}
                        color={getEstadoColor(cajaSeleccionada.estado)}
                      />
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Fechas */}
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Fecha Apertura
                      </Typography>
                      <Typography variant="body1">
                        {formatFecha(cajaSeleccionada.fecha_apertura)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="text.secondary">
                        Fecha Cierre
                      </Typography>
                      <Typography variant="body1">
                        {formatFecha(cajaSeleccionada.fecha_cierre)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Resumen Financiero */}
              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Resumen Financiero
              </Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined" sx={{ bgcolor: 'info.light' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AccountBalanceWallet sx={{ mr: 1 }} />
                        <Typography variant="subtitle2">
                          Monto Inicial
                        </Typography>
                      </Box>
                      <Typography variant="h6">
                        {formatMonto(cajaSeleccionada.monto_inicial)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined" sx={{ bgcolor: 'success.light' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <Receipt sx={{ mr: 1 }} />
                        <Typography variant="subtitle2">
                          Total Recaudado
                        </Typography>
                      </Box>
                      <Typography variant="h6">
                        {formatMonto(cajaSeleccionada.total_recaudado)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Card variant="outlined" sx={{ bgcolor: 'warning.light' }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AttachMoney sx={{ mr: 1 }} />
                        <Typography variant="subtitle2">
                          Monto Total
                        </Typography>
                      </Box>
                      <Typography variant="h6">
                        {formatMonto(cajaSeleccionada.monto_total)}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              {/* Boletas */}
              <Typography variant="h6" gutterBottom>
                Boletas ({boletas.length})
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Tarifa</TableCell>
                      <TableCell>Monto</TableCell>
                      <TableCell>Fecha/Hora</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {boletas.length > 0 ? (
                      boletas.map((boleta) => (
                        <TableRow key={boleta.id}>
                          <TableCell>{boleta.id}</TableCell>
                          <TableCell>{boleta.tarifa?.nombre || '-'}</TableCell>
                          <TableCell>{formatMonto(boleta.monto)}</TableCell>
                          <TableCell>{formatFecha(boleta.fecha_hora)}</TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          No hay boletas registradas
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
